import axios from 'axios';
import { isEmpty } from 'lodash';
import { getFacebookCookies, getTikTokTracking } from '../helpers/trackingHelper';
import * as Sentry from '@sentry/react';

/**
 * @param {number|string} funnelNr
 * @param {Object} [options]
 * @param {string} [options.leadCode] - When the lead answered India in the quiz,
 *   the backend prices the funnel in INR and marks it `countryCode: 'IN'`
 *   (unlocks UPI at checkout).
 */
export const getFunnel = async (funnelNr = 1, { leadCode } = {}) => {
  // lodash isEmpty(35) is true, so a numeric id must not go through it.
  if (typeof funnelNr !== 'number' && isEmpty(funnelNr)) {
    funnelNr = 1;
  }
  try {
    const funnelApi = `${process.env.REACT_APP_API_URL}funnels/get/${funnelNr}`;
    const response = await axios.get(funnelApi, {
      params: leadCode ? { code: leadCode } : undefined,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    });

    if (!response.data) {
      throw new Error('No data received from the server');
    }

    return response.data;
  } catch (error) {
    if (error.response) {
      console.error('Server Error:', error.response.data?.message || 'Server error occurred');
    } else if (error.request) {
      console.error('Network Error: No response received from server');
    } else {
      console.error('Request Error:', error.message);
    }
    throw error;
  }
};

/**
 * @param {string} skillType -"hard_skills" or "soft_skills" or "professional_certifications"
 * @param {number} jobTitleId - The ID of the job title
 * @returns {Promise<Array>} - Array of skills for the given type
 */
export const getSkills = async (skillType, jobTitleId) => {
  try {
    const response = await fetch(`${process.env.REACT_APP_API_URL}funnels/occupation/profile/${jobTitleId}`);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    const skillToDataMap = {
      professional_certifications: data.data.certifications,
      soft_skills: data.data.soft_skills,
      hard_skills: data.data.hard_skills,
    };

    return skillToDataMap[skillType];
  } catch (error) {
    console.error('Error fetching skills:', error);
    return {
      data: {
        hard_skills: [],
        soft_skills: [],
      },
      error: error.message || 'Failed to fetch skills',
    };
  }
};

/**
 * @typedef {Object} CreateLeadPayload
 * @property {string} email - User's email address
 * @property {number} funnel_id - ID of the funnel
 * @property {string} code - Unique code for the lead
 * @property {QuizAnswers} quiz_answers - Object containing all quiz answers
 */

/**
 * Creates a new lead with the provided payload
 * @param {CreateLeadPayload} payload - The lead creation payload
 * @returns {Promise<any>} The created lead data
 */
export const createLead = async (payload) => {
  try {
    const response = await fetch(`${process.env.REACT_APP_API_URL}funnels/leads`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to create lead');
    }
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('An unexpected error occurred while creating lead');
  }
};

export const getLead = async (leadCode) => {
  const response = await fetch(`${process.env.REACT_APP_API_URL}funnels/leads/${leadCode}`);

  if (response.status === 204) {
    return null;
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to get lead');
  }

  const result = await response.json();

  return result.data;
};

/**
 * Counts the jobs currently matching the lead's quiz answers and stores the
 * number on the lead (available_jobs_count).
 * @param {string} leadCode
 * @returns {Promise<{code: string, availableJobsCount: number} | null>}
 */
export const getLeadJobCount = async (leadCode) => {
  const response = await fetch(`${process.env.REACT_APP_API_URL}funnels/leads/job-count/${leadCode}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to count jobs for lead');
  }

  const result = await response.json();

  return result.data ?? null;
};

export const successPaypalPayment = async (leadCode, selectedProduct, details) => {
  const funnelApi = `${process.env.REACT_APP_API_URL}funnels/paypal/success`;
  try {
    const response = await axios.post(
      funnelApi,
      {
        lead_code: leadCode,
        product_sku: selectedProduct.sku,
        status: details.status,
        name: details.payer.name.given_name,
        email: details.payer.email_address,
        id: details.id,
        amount: details.purchase_units[0].amount.value,
        currency: details.purchase_units[0].amount.currency_code,
        payer_id: details.payer.payer_id,
        vault_id: details.vault_id,
      },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
  } catch (error) {
    console.error('Error saving paypal payment:', error);
    throw error;
  }
};

export const facebookPixelPurchase = async (leadCode, userEmail, selectedProduct, eventId) => {
  const funnelApi = `${process.env.REACT_APP_API_URL}funnels/track/facebook`;
  const amount = selectedProduct?.prices[0]?.amount;
  const currency = selectedProduct?.prices[0]?.currency;
  const productSku = selectedProduct?.sku;

  try {
    await axios.post(
      funnelApi,
      {
        code: leadCode,
        email: userEmail,
        product_sku: productSku,
        amount: amount,
        currency: currency,
        event_id: eventId,
        facebook_data: getFacebookCookies(),
      },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
  } catch (error) {
    console.error('Error saving facebook pixel purchase:', error);
  }
};

export const tiktokPixelPurchase = async (beOptions = {}) => {
  const funnelApi = `${process.env.REACT_APP_API_URL}funnels/track/tiktok/purchase`;
  const { code, email, product_sku, currency, amount, event_id } = beOptions;

  try {
    await axios.post(
      funnelApi,
      {
        code,
        email,
        product_sku,
        amount,
        currency,
        event_id,
        tiktok_data: getTikTokTracking(),
      },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
  } catch (error) {
    console.error('Error saving tiktok pixel purchase:', error);
  }
};

export const getOrderSummary = async (leadCode) => {
  const response = await fetch(`${process.env.REACT_APP_API_URL}funnels/order/summary/${leadCode}`);

  if (response.status === 204) {
    return null;
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to get order summary');
  }

  const result = await response.json();

  return result.data;
};

/**
 * Sends password reset request to backend
 * @param {string} email - User's email address
 * @returns {Promise<void>} Resolves on success (204), throws on error
 */
export const requestPasswordReset = async (email) => {
  try {
    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}funnels/firebase/reset-password`,
      { email },
      {
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      }
    );

    // 204 No Content means success
    if (response.status === 204) {
      return true;
    }
  } catch (error) {
    Sentry.captureException(error, {
      tags: { error_type: 'password_reset_failed' },
      extra: { email: email },
    });
    if (error.response?.status === 400) {
      throw new Error(error.response.data);
    } else {
      throw new Error('Failed to send password reset email. Please try again.');
    }
  }
};

/**
 * Creates a Primer client session for payment processing
 * @param {Object} payload - The payment payload
 * @param {string} payload.orderId - Unique order identifier
 * @param {string} payload.currencyCode - Currency code (e.g., "USD", "GBP")
 * @param {number} payload.amount - Amount in cents
 * @param {Array} payload.lineItems - Array of line items
 * @param {Object} payload.metadata - Additional metadata for the order
 * @returns {Promise<any>} The Primer client session data with clientToken
 */
export const createPrimerClientSession = async (amount, currency, productKey, leadCode, brandName) => {
  try {
    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}primer/client-session`,
      {
        amount: amount,
        currencyCode: currency,
        productKey: productKey,
        leadCode: leadCode,
        brandName: brandName,
      },
      {
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      }
    );

    return response.data.data;
  } catch (error) {
    Sentry.captureException(error, {
      tags: { error_type: 'primer_client_session_failed' },
      extra: { amount: amount, currency: currency, productKey: productKey, leadCode: leadCode },
    });

    if (error.response?.status === 400) {
      throw new Error(error.response.data?.message || 'Invalid payment request');
    } else if (error.response?.status === 500) {
      throw new Error('Payment service temporarily unavailable. Please try again.');
    } else {
      throw new Error('Failed to initialize payment. Please try again.');
    }
  }
};

/**
 * Charges an upsell using Primer with vaulted payment method
 * @param {string} code - Lead code
 * @param {string} sku - Product SKU
 * @param {number} amount - Amount in cents
 * @param {string} currency - Currency code (e.g., "USD", "GBP")
 * @param {string} paymentMethodToken - Vaulted payment method token (optional)
 * @param {string} customerId - Customer ID (optional)
 * @returns {Promise<any>} The upsell charge response
 */
export const chargeUpsell = async (code, sku, amount, currency, brandName) => {
  try {
    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}primer/charge/upsell`,
      {
        code: code,
        sku: sku,
        amount: amount,
        currencyCode: currency,
        brandName: brandName,
      },
      {
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      }
    );

    return response.data.data;
  } catch (error) {
    Sentry.captureException(error, {
      tags: { error_type: 'upsell_charge_failed' },
      extra: { code, sku, amount, currency },
    });

    if (error.response?.status === 400) {
      throw new Error(error.response.data?.message || 'Invalid upsell payment request');
    } else if (error.response?.status === 500) {
      throw new Error('Upsell payment service temporarily unavailable. Please try again.');
    } else {
      throw new Error('Failed to process upsell payment. Please try again.');
    }
  }
};

/**
 * Creates a Stripe payment intent for the given product and lead
 * @param {Object} params - Payment intent parameters
 * @param {number} params.amount - Amount in cents
 * @param {string} params.currency - Currency code
 * @param {string} params.productKey - Product SKU
 * @param {string} params.leadCode - Lead code
 * @param {boolean} params.createLead - Whether to create a lead
 * @param {string} params.email - Lead email
 * @param {string} params.brandName - Brand name
 * @returns {Promise<Object>} Payment intent response
 */
export const createStripePaymentIntent = async ({
  amount,
  currency,
  productKey,
  leadCode,
  createLead,
  email,
  brandName,
}) => {
  try {
    const response = await axios.post(`${process.env.REACT_APP_API_URL}funnels/payment/create-intent`, {
      amount,
      currency,
      metadata: {
        product_key: productKey,
        lead_code: leadCode,
        create_lead: createLead,
        email,
        brand_name: brandName,
      },
    });
    return response.data;
  } catch (error) {
    Sentry.captureException(error, {
      tags: { error_type: 'stripe_payment_intent_failed' },
      extra: { amount, currency, productKey, leadCode },
    });

    if (error.response?.status === 400) {
      throw new Error(error.response.data?.message || 'Invalid payment request');
    } else if (error.response?.status === 500) {
      throw new Error('Payment service temporarily unavailable. Please try again.');
    } else {
      throw new Error('Failed to initialize payment. Please try again.');
    }
  }
};
